import { apiClient } from "@/lib/api/client";
import { ENDPOINTS } from "@/lib/api/config";
// PostHog captures for collection events fire server-side via log_activity() in
// services/api/functions/collections/lambda_function.py. No client capture needed.
import type {
  Collection,
  CollectionInviteLink,
  CollectionInviteLinks,
  CollectionItem,
  CollectionMember,
  CollectionSort,
  CreateCollectionRequest,
  UpdateCollectionRequest,
  AddItemsRequest,
  RemoveItemsRequest,
  InviteMemberRequest,
  CollectionItemType,
  CollectionRole,
  CursorPage,
} from "@/types/collections";

class CollectionsService {
  private static instance: CollectionsService;

  static getInstance(): CollectionsService {
    if (!CollectionsService.instance) {
      CollectionsService.instance = new CollectionsService();
    }
    return CollectionsService.instance;
  }

  // ---- Collection CRUD ----

  async listCollections(opts?: { cursor?: string; limit?: number; sort?: CollectionSort }): Promise<CursorPage<Collection>> {
    const params = new URLSearchParams();
    if (opts?.cursor) params.set("cursor", opts.cursor);
    if (opts?.limit) params.set("limit", String(opts.limit));
    if (opts?.sort) params.set("sort", opts.sort);
    const qs = params.toString();
    return apiClient.request<CursorPage<Collection>>(
      `${ENDPOINTS.COLLECTIONS.LIST}${qs ? `?${qs}` : ""}`,
    );
  }

  async getCollection(id: string, skipAuth = false, invite?: string): Promise<Collection> {
    const qs = invite ? `?invite=${encodeURIComponent(invite)}` : "";
    return apiClient.request<Collection>(`${ENDPOINTS.COLLECTIONS.GET(id)}${qs}`, {}, skipAuth);
  }

  async createCollection(input: CreateCollectionRequest): Promise<Collection> {
    const collection = await apiClient.request<Collection>(ENDPOINTS.COLLECTIONS.CREATE, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return collection;
  }

  async updateCollection(id: string, input: UpdateCollectionRequest): Promise<Collection> {
    const result = await apiClient.request<Collection>(ENDPOINTS.COLLECTIONS.UPDATE(id), {
      method: "PUT",
      body: JSON.stringify(input),
    });
    return result;
  }

  async deleteCollection(id: string): Promise<void> {
    await apiClient.request(ENDPOINTS.COLLECTIONS.DELETE(id), { method: "DELETE" });
  }

  // ---- Items ----

  async listItems(
    collectionId: string,
    opts?: { cursor?: string; order?: "asc" | "desc"; limit?: number; hydrate?: boolean; skipAuth?: boolean; invite?: string; item_type?: "stash" | "entity" },
  ): Promise<CursorPage<CollectionItem>> {
    const params = new URLSearchParams();
    if (opts?.cursor) params.set("cursor", opts.cursor);
    if (opts?.limit) params.set("limit", String(opts.limit));
    if (opts?.order) params.set("order", opts.order);
    if (opts?.hydrate) params.set("hydrate", "true");
    if (opts?.invite) params.set("invite", opts.invite);
    if (opts?.item_type) params.set("item_type", opts.item_type);
    const qs = params.toString();
    return apiClient.request<CursorPage<CollectionItem>>(
      `${ENDPOINTS.COLLECTIONS.ITEMS(collectionId)}${qs ? `?${qs}` : ""}`,
      {},
      opts?.skipAuth,
    );
  }

  /** A collection's stashes, newest first and without content: like a tag's post ids, cards read by id. */
  async listStashes(
    collectionId: string,
    opts?: { skipAuth?: boolean; invite?: string },
  ): Promise<CursorPage<CollectionItem>> {
    const items: CollectionItem[] = [];
    let cursor: string | undefined;
    do {
      const page = await this.listItems(collectionId, { ...opts, cursor, limit: 1000, order: "desc", item_type: "stash" });
      items.push(...page.items);
      cursor = page.next_cursor ?? undefined;
    } while (cursor);
    return { items, next_cursor: null };
  }

  async addItems(collectionId: string, items: Array<{ type: CollectionItemType; id: string }>): Promise<{ added: number; item_count: number; stash_count: number }> {
    const BATCH_SIZE = 50;
    let result: { added: number; item_count: number; stash_count: number };
    if (items.length <= BATCH_SIZE) {
      result = await apiClient.request<{ added: number; item_count: number; stash_count: number }>(
        `${ENDPOINTS.COLLECTIONS.ITEMS(collectionId)}`,
        { method: "POST", body: JSON.stringify({ items }) },
      );
    } else {
      let totalAdded = 0;
      let itemCount = 0;
      let stashCount = 0;
      for (let i = 0; i < items.length; i += BATCH_SIZE) {
        const batch = items.slice(i, i + BATCH_SIZE);
        const res = await apiClient.request<{ added: number; item_count: number; stash_count: number }>(
          `${ENDPOINTS.COLLECTIONS.ITEMS(collectionId)}`,
          { method: "POST", body: JSON.stringify({ items: batch }) },
        );
        totalAdded += res.added;
        itemCount = res.item_count;
        stashCount = res.stash_count;
      }
      result = { added: totalAdded, item_count: itemCount, stash_count: stashCount };
    }
    return result;
  }

  async removeItems(collectionId: string, itemKeys: string[]): Promise<{ removed: number; item_count: number; stash_count: number }> {
    const result = await apiClient.request<{ removed: number; item_count: number; stash_count: number }>(
      `${ENDPOINTS.COLLECTIONS.ITEMS(collectionId)}`,
      { method: "DELETE", body: JSON.stringify({ item_keys: itemKeys }) },
    );
    return result;
  }

  // ---- Recommendations ----

  async getRecommendations(id: string): Promise<import("@/types/collections").RecommendationsResponse> {
    return apiClient.request(ENDPOINTS.COLLECTIONS.RECOMMENDATIONS(id));
  }

  // ---- Reverse lookups ----

  async getCollectionsForStash(stashId: string): Promise<CursorPage<Collection>> {
    return apiClient.request<CursorPage<Collection>>(ENDPOINTS.COLLECTIONS.FOR_STASH(stashId));
  }

  async getCollectionsForEntity(entityId: string): Promise<CursorPage<Collection>> {
    return apiClient.request<CursorPage<Collection>>(ENDPOINTS.COLLECTIONS.FOR_ENTITY(entityId));
  }

  // ---- Members ----

  async listMembers(collectionId: string): Promise<{ items: CollectionMember[] }> {
    return apiClient.request(`${ENDPOINTS.COLLECTIONS.MEMBERS(collectionId)}`);
  }

  async inviteMember(collectionId: string, userId: string, role: CollectionRole): Promise<{ status: string; invited_at: string }> {
    const result = await apiClient.request<{ status: string; invited_at: string }>(
      ENDPOINTS.COLLECTIONS.MEMBERS(collectionId),
      { method: "POST", body: JSON.stringify({ user_id: userId, role }) },
    );
    return result;
  }

  async updateMemberRole(collectionId: string, userId: string, role: CollectionRole): Promise<{ success: boolean }> {
    return apiClient.request(ENDPOINTS.COLLECTIONS.MEMBER(collectionId, userId), {
      method: "PUT",
      body: JSON.stringify({ role }),
    });
  }

  async acceptInvite(collectionId: string, userId: string): Promise<{ success: boolean }> {
    return apiClient.request(ENDPOINTS.COLLECTIONS.MEMBER(collectionId, userId), {
      method: "PUT",
      body: JSON.stringify({ status: "active" }),
    });
  }

  async declineInvite(collectionId: string, userId: string): Promise<{ success: boolean }> {
    return apiClient.request(ENDPOINTS.COLLECTIONS.MEMBER(collectionId, userId), {
      method: "PUT",
      body: JSON.stringify({ status: "declined" }),
    });
  }

  async inviteExternal(collectionId: string, email: string, role: CollectionRole): Promise<{ status: string; email: string }> {
    const result = await apiClient.request<{ status: string; email: string }>(
      ENDPOINTS.COLLECTIONS.INVITE_EXTERNAL(collectionId),
      { method: "POST", body: JSON.stringify({ email, role }) },
    );
    return result;
  }

  async removeMember(collectionId: string, userId: string): Promise<{ removed: boolean }> {
    return apiClient.request(ENDPOINTS.COLLECTIONS.MEMBER(collectionId, userId), {
      method: "DELETE",
    });
  }

  // ---- Invite link ----

  async getInviteLink(collectionId: string): Promise<{ invite_links: CollectionInviteLinks }> {
    return apiClient.request(ENDPOINTS.COLLECTIONS.INVITE_LINK(collectionId));
  }

  async generateInviteLink(collectionId: string, role: CollectionRole = "viewer"): Promise<CollectionInviteLink> {
    return apiClient.request(ENDPOINTS.COLLECTIONS.INVITE_LINK(collectionId), {
      method: "POST",
      body: JSON.stringify({ role }),
    });
  }

  async revokeInviteLink(collectionId: string, role: CollectionRole): Promise<{ revoked: boolean }> {
    return apiClient.request(
      `${ENDPOINTS.COLLECTIONS.INVITE_LINK(collectionId)}?role=${encodeURIComponent(role)}`,
      { method: "DELETE" },
    );
  }

  // ---- Invite claim ----

  async claimInvite(collectionId: string, inviteCode: string): Promise<{ status: string; role?: string }> {
    const result = await apiClient.request<{ status: string; role?: string }>(
      ENDPOINTS.COLLECTIONS.CLAIM_INVITE(collectionId),
      { method: "POST", body: JSON.stringify({ invite_code: inviteCode }) },
    );
    return result;
  }

  // ---- Follow ----

  async followCollection(id: string): Promise<Collection> {
    const collection = await apiClient.request<Collection>(ENDPOINTS.COLLECTIONS.FOLLOW(id), {
      method: "POST",
    });
    return collection;
  }

  async unfollowCollection(id: string): Promise<{ unfollowed: boolean }> {
    return apiClient.request(ENDPOINTS.COLLECTIONS.FOLLOW(id), {
      method: "DELETE",
    });
  }

  // ---- Public ----

  async listPublicCollections(opts?: { cursor?: string; limit?: number }): Promise<CursorPage<Collection>> {
    const params = new URLSearchParams();
    if (opts?.cursor) params.set("cursor", opts.cursor);
    if (opts?.limit) params.set("limit", String(opts.limit));
    const qs = params.toString();
    return apiClient.request<CursorPage<Collection>>(
      `${ENDPOINTS.COLLECTIONS.PUBLIC}${qs ? `?${qs}` : ""}`,
      {},
      true, // skipAuth for public endpoint
    );
  }
}

export const collectionsService = CollectionsService.getInstance();
